import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Star, Award, Users, Clock } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { heroSection, siteInfo, services, testimonials, whyChooseUs, aboutContent } from '../data/siteData';
import { blogPosts } from '../data/blogData';

const HomePage = ({ onBookingClick }) => {
  const [emblaRefTestimonials, emblaApiTestimonials] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'center',
      slidesToScroll: 1,
      skipSnaps: false
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApiTestimonials) return;
    setSelectedIndex(emblaApiTestimonials.selectedScrollSnap());
  }, [emblaApiTestimonials]);

  useEffect(() => {
    if (!emblaApiTestimonials) return;
    onSelect();
    emblaApiTestimonials.on('select', onSelect);
    emblaApiTestimonials.on('reInit', onSelect);
  }, [emblaApiTestimonials, onSelect]);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Full Screen from Top */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-black pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&q=85"
            alt="LookWell Gents Salon Interior"
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        {/* Centered Content */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            {/* Top Badge */}
            <p className="text-amber-500 text-xs sm:text-sm uppercase tracking-widest font-semibold mb-6 animate-fade-in-up">
              Excellence in Every Detail
            </p>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              LOOKWELL GENTS SALON
            </h1>

            {/* Tagline */}
            <p className="text-xl sm:text-2xl lg:text-3xl text-white mb-8 font-light animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              Where Precision Meets Distinction.
            </p>

            {/* Address & Phone */}
            <p className="text-sm sm:text-base text-gray-300 mb-10 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              Blue Building, Al Nahda 2, Dubai, UAE • APPOINTMENT: <span className="text-amber-500">+971 564 740 785</span>
            </p>

            {/* CTA Button */}
            <button
              onClick={onBookingClick}
              className="inline-block bg-white text-black px-10 py-5 text-lg font-bold uppercase tracking-wider hover:bg-amber-500 hover:text-black transition-all duration-300 transform hover:scale-105 shadow-2xl animate-fade-in-up"
              style={{ animationDelay: '0.6s' }}
            >
              Reserve Your Session
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Quick About Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="flex flex-col">
              <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Welcome to Excellence</p>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Dubai's Premier Gents Salon
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                {aboutContent.description}
              </p>
              <p className="text-gray-600 text-lg mb-8">
                {aboutContent.mission}
              </p>
              <Link
                to="/about"
                className="inline-block bg-black text-white px-8 py-3 font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors self-start"
              >
                Learn More About Us
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1629881543350-9c14f791df99?w=1200&q=85"
                alt="LookWell Salon Professional Service"
                className="w-full h-auto max-h-[450px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://www.rapidhairsalon.ae/img/7-cl.png)' }}
        >
          <div className="absolute inset-0 bg-white/90"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">What We Offer</p>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Our Premium Services
            </h2>
            <p className="text-gray-600 text-lg w-full">
              Comprehensive grooming solutions designed for Dubai's modern gentleman
            </p>
          </div>

          {/* Services Grid - 2x2 Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(services).map(([key, service], index) => {
              // Convert key to slug: hairCare -> hair-care
              const slug = key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
              
              return (
                <Link
                  key={index}
                  to={`/services/${slug}`}
                  className="relative h-96 overflow-hidden group cursor-pointer shadow-xl hover:shadow-2xl transition-shadow duration-300"
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundImage: `url(${service.image})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
                  </div>

                  {/* Content Overlay */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-8">
                    <div>
                      <h3 className="text-3xl font-bold text-white mb-3">{service.title}</h3>
                      <p className="text-gray-200 text-sm mb-4">{service.subtitle}</p>
                      <span className="text-amber-500 font-bold uppercase text-sm tracking-wider inline-flex items-center gap-2">
                        View Details →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Why LookWell</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              The LookWell Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blogs Section */}
      <section className="py-20 bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Latest Updates</p>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              From Our Blog
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Stay updated with the latest grooming trends, tips, and news from LookWell Gents Salon
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <Link to={`/blog/${post.slug}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-500 text-black px-3 py-1 text-xs font-bold uppercase">{post.category}</span>
                    </div>
                  </div>
                </Link>
                <div className="p-6">
                  <p className="text-gray-500 text-sm mb-3">{post.date} • {post.readTime}</p>
                  <Link to={`/blog/${post.slug}`}>
                    <h3 className="text-xl font-bold text-black mb-3 hover:text-amber-500 transition-colors">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.slug}`} className="inline-flex items-center text-amber-500 font-semibold hover:text-amber-600 transition-colors">
                    Read More 
                    <span className="ml-2">→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Client Reviews</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="overflow-hidden -mx-4" ref={emblaRefTestimonials}>
            <div className="flex items-center">
              {testimonials.map((testimonial, index) => {
                const isCenter = index === selectedIndex % testimonials.length;
                const bgColor = isCenter ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gray-900';
                const textColor = isCenter ? 'text-black' : 'text-white';
                const serviceColor = isCenter ? 'text-black/80' : 'text-amber-500';
                const starColor = isCenter ? 'fill-black text-black' : 'fill-amber-500 text-amber-500';
                const quoteColor = isCenter ? 'text-black/20' : 'text-gray-700';
                
                return (
                  <div 
                    key={testimonial.id} 
                    className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4 transition-all duration-500"
                    style={{
                      filter: isCenter ? 'blur(0px)' : 'blur(4px)',
                      opacity: isCenter ? 1 : 0.4,
                      transform: isCenter ? 'scale(1.05)' : 'scale(0.95)'
                    }}
                  >
                    <div className={`${bgColor} p-8 shadow-2xl h-full relative overflow-hidden rounded-lg`}>
                      {/* Decorative Element */}
                      <div className={`absolute top-0 right-0 w-32 h-32 ${isCenter ? 'bg-white/10' : 'bg-amber-500/10'} rounded-full -mr-16 -mt-16`}></div>
                      
                      {/* Name and Role */}
                      <div className="mb-4">
                        <h4 className={`font-bold text-xl mb-1 ${textColor}`}>{testimonial.name}</h4>
                        <p className={`text-sm ${textColor} opacity-80`}>{testimonial.role}</p>
                        <p className={`text-xs font-semibold uppercase tracking-wide mt-2 ${serviceColor}`}>
                          {testimonial.service}
                        </p>
                      </div>

                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => {
                          const fillStar = i < Math.floor(testimonial.rating);
                          const halfStar = i === Math.floor(testimonial.rating) && testimonial.rating % 1 !== 0;
                          
                          return (
                            <div key={i} className="relative">
                              <Star size={18} className={fillStar ? starColor : `text-gray-300`} fill={fillStar ? 'currentColor' : 'none'} />
                              {halfStar && (
                                <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
                                  <Star size={18} className={starColor} fill="currentColor" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Quote */}
                      <p className={`${textColor} relative z-10 text-base leading-relaxed`}>
                        "{testimonial.text}"
                      </p>

                      {/* Quote Icon */}
                      <div className={`absolute bottom-4 right-4 ${quoteColor} text-6xl font-serif leading-none`}>"</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1622287162716-f311baa1a2b8?w=1920&q=85)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          {/* Decorative Line */}
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Ready to Look Your Best?
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 w-full">
            Book your appointment today and experience the LookWell difference
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button
              onClick={onBookingClick}
              className="group relative bg-amber-500 text-black px-10 py-5 text-lg font-bold uppercase tracking-wider overflow-hidden transition-all duration-300 hover:bg-amber-600 hover:scale-105 shadow-2xl"
            >
              <span className="relative z-10">Book Appointment</span>
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </button>
            <Link
              to="/contact"
              className="group relative bg-white text-black px-10 py-5 text-lg font-bold uppercase tracking-wider border-2 border-white hover:bg-transparent hover:text-white transition-all duration-300 shadow-2xl"
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-amber-500/30 rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 border-4 border-amber-500/30 rounded-full"></div>
      </section>
    </div>
  );
};

export default HomePage;
